import { createSlice } from "@reduxjs/toolkit";
import { apiSlice } from "../../app/apiSlice";

// Student Classes API endpoints
const studentClassesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Get live classes for student
    getStudentLiveClasses: builder.query({
      query: ({
        limit = 10,
        offset = 0,
        startTime,
        endTime,
        searchText = "",
        moduleType = "",
      } = {}) => {
        let url = `/api/v1/en/student/live-classes/get-all?limit=${limit}&offset=${offset}`;
        if (startTime) url += `&startTime=${startTime}`;
        if (endTime) url += `&endTime=${endTime}`;
        if (searchText && searchText.trim() !== "") {
          url += `&searchText=${encodeURIComponent(searchText.trim())}`;
        }
        if (moduleType && moduleType.trim() !== "" && moduleType !== "all") {
          url += `&moduleType=${encodeURIComponent(moduleType.trim())}`;
        }
        return url;
      },
      providesTags: ["StudentLiveClasses"],
      transformResponse: (response) => {
        console.log("Live classes response:", response);
        // Transform the response to match component expectations
        if (response?.data?.data?.classes) {
          return {
            data: response.data.data.classes,
            totalCount: response.data.data.countDocuments,
            // Extract status-based counts from backend if available
            liveCount: response.data.data.liveCount || 0,
            upcomingCount: response.data.data.upcomingCount || 0,
            // Extract module type counts from backend
            moduleTypeCounts:
              response.data.data.liveClassCountsByModuleType || {},
            success: response.data.success,
            message: response.data.message,
          };
        }
        return {
          data: [],
          totalCount: 0,
          liveCount: 0,
          upcomingCount: 0,
          moduleTypeCounts: {},
          success: 0,
          message: "No data received",
        };
      },
    }),

    // Get recorded classes for student
    getStudentRecordedClasses: builder.query({
      // Accept optional searchText and courseModuleId so the backend can perform server-side filtering by module id
      query: ({ limit, offset, searchText = "", courseModuleId = "" } = {}) => {
        // When searching, don't include limit/offset to get all results
        const hasSearch = searchText && searchText.trim() !== "";

        let url = `/api/v1/en/student/recorded-lectures/get-all`;
        const params = [];

        // Only add limit/offset if not searching
        if (!hasSearch) {
          params.push(`limit=${limit !== undefined ? limit : 10}`);
          params.push(`offset=${offset !== undefined ? offset : 0}`);
        }

        if (hasSearch) {
          params.push(`searchText=${encodeURIComponent(searchText.trim())}`);
        }

        // Prefer filtering by exact module id when provided
        if (courseModuleId && courseModuleId.trim() !== "") {
          params.push(
            `courseModuleId=${encodeURIComponent(courseModuleId.trim())}`
          );
        }

        if (params.length > 0) {
          url += `?${params.join("&")}`;
        }

        return url;
      },
      providesTags: ["StudentRecordedClasses"],
      transformResponse: (response) => {
        console.log("Recorded classes response:", response);
        // Transform the response to match component expectations
        if (response?.data?.data?.lectures) {
          return {
            data: response.data.data.lectures,
            totalCount: response.data.data.countDocuments,
            // Support both naming shapes: prefer explicit moduleCounts if present, otherwise map lectureCountsByModuleType
            moduleCounts:
              response.data.data.moduleCounts ||
              response.data.data.lectureCountsByModuleType ||
              null,
            // Backend also returns totals for notes and handouts; include them when present
            totalNotes:
              typeof response.data.data.totalNotes === "number"
                ? response.data.data.totalNotes
                : null,
            totalHandouts:
              typeof response.data.data.totalHandouts === "number"
                ? response.data.data.totalHandouts
                : null,
            success: response.data.success,
            message: response.data.message,
          };
        }
        return {
          data: [],
          totalCount: 0,
          moduleCounts: null,
          totalNotes: null,
          totalHandouts: null,
          success: 0,
          message: "No data received",
        };
      },
    }),

    // View specific live class
    viewLiveClass: builder.query({
      query: (classId) => `/api/v1/en/student/live-classes/view/${classId}`,
      providesTags: (result, error, classId) => [
        { type: "LiveClassDetail", id: classId },
      ],
    }),

    // View specific recorded lecture
    viewRecordedLecture: builder.query({
      query: (lectureId) =>
        `/api/v1/en/student/recorded-lectures/view/${lectureId}`,
      providesTags: (result, error, lectureId) => [
        { type: "RecordedLectureDetail", id: lectureId },
      ],
    }),

    // Get course materials for student
    getStudentCourseMaterials: builder.query({
      query: ({ limit = 20, offset = 0, searchText = "" } = {}) => {
        let url = `/api/v1/en/student/course/materials?limit=${limit}&offset=${offset}`;
        if (searchText && searchText.trim() !== "") {
          url += `&searchText=${encodeURIComponent(searchText.trim())}`;
        }
        return url;
      },
      providesTags: ["StudentCourseMaterials"],
      transformResponse: (response) => {
        console.log("Course materials response:", response);
        // Transform the response to match component expectations
        if (response?.data?.data?.list) {
          return {
            data: response.data.data.list,
            totalCount: response.data.data.count || 0,
            success: response.data.success,
            message: response.data.message,
          };
        }
        return {
          data: [],
          totalCount: 0,
          success: 0,
          message: "No data received",
        };
      },
    }),



    // Create live class attendance
    createLiveClassAttendance: builder.mutation({
      query: (attendanceData) => ({
        url: "/api/v1/en/student/live-class/attendance/create",
        method: "POST",
        body: attendanceData,
      }),
      invalidatesTags: ["StudentLiveClasses"],
    }),
  }),
});

// Student Classes slice for local state management
const studentClassesSlice = createSlice({
  name: "studentClasses",
  initialState: {
    activeTab: "live", // 'live' or 'recorded'
    searchTerm: "",
    sortBy: "date",
    sortOrder: "desc",
    viewMode: "grid", // 'grid' or 'list'
  },
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
    setViewMode: (state, action) => {
      state.viewMode = action.payload;
    },
  },
});

// Export hooks
export const {
  useGetStudentLiveClassesQuery,
  useGetStudentRecordedClassesQuery,
  useViewLiveClassQuery,
  useViewRecordedLectureQuery,
    useGetStudentCourseMaterialsQuery,
  useCreateLiveClassAttendanceMutation,
} = studentClassesApiSlice;

// Export actions
export const {
  setActiveTab,
  setSearchTerm,
  setSortBy,
  setSortOrder,
  setViewMode,
} = studentClassesSlice.actions;

// Export reducer
export default studentClassesSlice.reducer;
